<template>
  <div>
    <page-loading
      v-if="loading"
      message="Carregando parceiros"
      subtitle="Buscando a lista atualizada"
    />
    <template v-else>
    <div class="header">
      <v-row align="center">
        <v-col v-if="!hasNoFranchiseFilterProfile" cols="12" md="4">
        <v-select
            clearable
            :items="sessionFranchises"
            v-model="franchiseId"
            :item-text="displayTextFranchise"
            item-value="id"
            :label="hasNoFranchiseFilterProfile ? 'Cidade' : 'Franquia'"
          ></v-select>
        </v-col>
        <v-col v-if="canTrackAssociates" cols="12" md="4">
          <v-select
            :items="associateFilterItems"
            v-model="associateId"
            item-text="text"
            item-value="value"
            label="Colaborador"
            :disabled="!hasNoFranchiseFilterProfile && !franchiseId"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            clearable
            :items="statusFilterItems"
            v-model="statusFilter"
            item-text="text"
            item-value="value"
            label="Situação"
          ></v-select>
        </v-col>
        <v-col
          cols="12"
          :md="canTrackAssociates ? 1 : 5"
          class="d-flex justify-end align-center ml-auto"
        >
          <v-btn
            v-if="!isFranchiseAssociateInactive && !isFranchiseAssociatePending"
            color="#e5183d"
            dark
            rounded
            @click="openNewPartnerDialog"
          >
            Novo parceiro
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <premium-upsell-banner
      v-if="showPremiumUpsell"
      class="mb-4"
      variant="compact"
      title="Cadastre parceiros e monte sua rede — desbloqueie comissões Premium"
      subtitle="Associados Premium recebem comissões quando a rede adere ao plano."
      cta-label="Ver potencial na rede"
      :show-secondary="false"
      @cta="goToPremiumUpsell"
    />

    <v-card
      v-if="voucherInfo?.hasPartnerPremiumVoucher"
      outlined
      class="mb-4 voucher-card"
    >
      <v-card-text class="d-flex flex-wrap align-center justify-space-between">
        <div>
          <div class="voucher-card__title">
            <v-icon color="#e5183d" class="mr-2">mdi-store</v-icon>
            Vouchers Premium para Parceiros
          </div>
          <p class="mb-0 mt-2 grey--text text--darken-1">
            {{ voucherInfo.partnerMessage }}
          </p>
        </div>
        <v-btn
          color="#e5183d"
          dark
          :disabled="!voucherInfo.canDistributePartner"
          @click="openGrantPartnerVoucherDialog"
        >
          Conceder voucher
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquise pelo nome, CNPJ, CPF, email, telefone ou cidade"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        :header-props="{ sortByText: 'Filtrar por' }"
        :headers="headers"
        :items="partners"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-text': 'parceiros por página' }"
        :search="search"
        no-data-text="Nenhum parceiro encontrado"
        class="elevation-0"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td class="d-block d-sm-table-cell">{{ item.name }}</td>
              <td class="d-block d-sm-table-cell">{{ formatDocument(item.document, item.status) }}</td>
              <td class="d-block d-sm-table-cell">
                {{ phoneNumberFormat(item.phone) }}
              </td>
              <td class="d-block d-sm-table-cell">{{ item.email }}</td>
              <td class="d-block d-sm-table-cell">{{ partnerStatusLabel(item.status) }}</td>
              <td class="d-block d-sm-table-cell">
                <template v-if="item.address?.cityName || item.address?.state">
                  {{ item.address.cityName }} - {{ item.address.state }}
                </template>
                <span v-else class="grey--text">A definir</span>
              </td>

              <td class="d-block d-sm-table-cell">{{ formatDate(item.createdAt) }}</td>
              <td class="d-block d-sm-table-cell">
                <span
                  :class="['premium-badge', item.isPremium ? 'premium-badge--premium' : 'premium-badge--free']"
                >
                  {{ item.isPremium ? 'Premium' : 'Free' }}
                </span>
              </td>
              <td class="d-block d-sm-table-cell">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      size="24"
                      color="#e5183d"
                      @click="handleDetailPartner(item)"
                    >
                      mdi-store-cog
                    </v-icon>
                  </template>
                  <span>Configurar loja</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="openNewPartner" max-width="520">
      <v-card>
        <v-card-title class="headline">Novo parceiro</v-card-title>
        <v-card-text>
          <v-form>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="newPartner.name"
                  label="Nome"
                  dense
                  outlined
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newPartner.phone"
                  label="Telefone"
                  dense
                  outlined
                  v-mask="'(##) #####-####'"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newPartner.document"
                  label="CPF ou CNPJ"
                  dense
                  outlined
                  :v-mask="documentMask"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="newPartner.email"
                  label="E-mail"
                  dense
                  outlined
                  type="email"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="newPartner.useAssociateTax"
                  dense
                  color="#e5183d"
                  :disabled="!hasNoFranchiseFilterProfile && !franchiseId"
                >
                  <template #label>
                    <span class="checkbox-label">
                      Adesão do colaborador
                      <v-tooltip top>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            size="18"
                            color="#e5183d"
                            class="ml-2"
                          >
                            mdi-help-circle
                          </v-icon>
                        </template>
                        <span class="tooltip-text">
                          Caso fique desmarcado, o parceiro pagará apenas a adesão parcial de R$79,90 e
                          o associado não receberá a comissão de R$80,00.
                        </span>
                      </v-tooltip>
                    </span>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col
                v-if="voucherInfo?.canDistributePartner"
                cols="12"
              >
                <v-checkbox
                  v-model="usePartnerPremiumVoucher"
                  hide-details
                  class="mt-0"
                  label="Utilizar Voucher Premium para Parceiro"
                  :hint="'Será consumido 1 voucher do seu saldo (' + (voucherInfo?.partnerPremiumVoucherBalance ?? 0) + ' disponíveis)'"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-form>
          <p v-if="!hasNoFranchiseFilterProfile && !franchiseId" class="mt-3 red--text">
            Selecione uma {{ hasNoFranchiseFilterProfile ? "cidade" : "franquia" }} antes de cadastrar um parceiro.
          </p>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="closeNewPartnerDialog">Cancelar</v-btn>
          <v-btn
            color="#e5183d"
            dark
            :loading="savingPartner"
            @click="handleCreateSimplePartner"
            :disabled="(!hasNoFranchiseFilterProfile && !franchiseId) || savingPartner"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="openPartnerDetail" max-width="600">
      <v-card>
        <v-card-title class="headline">Detalhes do parceiro</v-card-title>
        <v-card-text>
          <v-row v-if="selectedPartner">
            <v-col cols="12" md="6">
              <p class="subtitle-1 mb-2">Nome</p>
              <p class="subtitle-2 font-weight-bold">{{ selectedPartner.name }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="subtitle-1 mb-2">Documento</p>
              <p class="subtitle-2 font-weight-bold">{{ formatDocument(selectedPartner.document ?? "") }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="subtitle-1 mb-2">Telefone</p>
              <p class="subtitle-2 font-weight-bold">{{ phoneNumberFormat(selectedPartner.phone) }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="subtitle-1 mb-2">Email</p>
              <p class="subtitle-2 font-weight-bold">{{ selectedPartner.email ?? "-" }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="subtitle-1 mb-2">Cidade/Estado</p>
              <p class="subtitle-2 font-weight-bold">
                <template v-if="selectedPartner.address?.cityName || selectedPartner.address?.state">
                  {{ selectedPartner.address.cityName }} - {{ selectedPartner.address.state }}
                </template>
                <span v-else class="grey--text">A definir</span>
              </p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="subtitle-1 mb-2">Data de cadastro</p>
              <p class="subtitle-2 font-weight-bold">{{ formatDate(selectedPartner.createdAt) }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p class="subtitle-1 mb-2">Premium</p>
              <p class="subtitle-2 font-weight-bold">
                <span
                  :class="['premium-badge', selectedPartner.isPremium ? 'premium-badge--premium' : 'premium-badge--free']"
                >
                  {{ selectedPartner.isPremium ? 'Premium' : 'Free' }}
                </span>
              </p>
            </v-col>
          </v-row>
          <div v-if="selectedPartner && !selectedPartner.isPremium && voucherInfo?.canDistributePartner" class="mt-4">
            <v-btn
              color="#e5183d"
              dark
              :loading="grantingPartnerVoucher"
              @click="handleGrantPartnerVoucher(selectedPartner.id)"
            >
              Conceder voucher Premium
            </v-btn>
            <p class="caption mt-2 mb-0 grey--text">
              O parceiro torna-se Premium imediatamente, sem cobrança.
            </p>
          </div>
          <div v-else-if="!selectedPartner">
            <p>Carregando dados...</p>
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="closePartnerDetail">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="grantPartnerVoucherOpen" max-width="520">
      <v-card>
        <v-card-title>Conceder voucher Premium para parceiro</v-card-title>
        <v-card-text>
          <v-select
            v-model="grantPartnerVoucherTargetId"
            :items="grantPartnerVoucherCandidates"
            item-text="label"
            item-value="id"
            label="Parceiro beneficiário"
            outlined
            dense
            hide-details
            no-data-text="Nenhum parceiro não Premium disponível"
          />
          <p class="caption mt-3 mb-0 grey--text">
            O parceiro torna-se Premium imediatamente, sem cobrança.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="grantPartnerVoucherOpen = false">Cancelar</v-btn>
          <v-btn
            color="#e5183d"
            dark
            :loading="grantingPartnerVoucher"
            :disabled="!grantPartnerVoucherTargetId"
            @click="handleGrantPartnerVoucherFromDialog"
          >
            Conceder
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import PremiumUpsellBanner from "@/components/premium/PremiumUpsellBanner.vue";

import { PartnerType } from "@/types/partner/Partner";
import { GetAllPartners } from "@/services/partners/GetAllPartners";
import { CreateSimplePartner, CreateSimplePartnerPayload } from "@/services/partners/CreateSimplePartner";
import { GetPremiumVouchers } from "@/services/premium/GetPremiumVouchers";
import { GrantPartnerPremiumVoucher } from "@/services/premium/GrantPartnerPremiumVoucher";
import { PremiumVoucherInfo } from "@/types/premium/PremiumVoucher";
import { GetAssociates } from "@/services/associates/GetAssociates";
import { format } from "date-fns";
import { SessionFranchiseType } from "@/types/user/SessionInfo";
import { AssociateType } from "@/types/associate/Associate";

interface SortItem {
  name: string;
  phone: string;
  createdAt: string;
}

interface PartnerWithDetail extends PartnerType {
  email?: string;
  document?: string;
}

export default defineComponent({
  components: { PageLoading, PremiumUpsellBanner },
  data() {
    return {
      mobileBreakpoint: 600,
      loading: true,
      showModal: false,
      showModalRemove: false,
      franchiseId: "",
      associateId: "" as string,
      statusFilter: "" as string,
      associates: [] as AssociateType[],
      partners: [] as PartnerWithDetail[],
      search: "",
      sortBy: [] as ReadonlyArray<SortItem>,
      sortDesc: false,
      noDataText: "Nenhum dado disponível",
      headers: [
        {
          text: "Nome",
          value: "name",
        },
        { text: "Documento", value: "document" },
        { text: "Telefone", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Situação", value: "status" },
        { text: "Cidade", value: "address.cityName" },
        { text: "Data de cadastro", value: "createdAt" },
        { text: "Premium", value: "isPremium" },
        {
          text: "Ações",
          value: "actions",
        },
      ],
    openNewPartner: false,
      openPartnerDetail: false,
      selectedPartner: null as PartnerWithDetail | null,
      savingPartner: false,
      newPartner: {
        name: "",
        phone: "",
        email: "",
        document: "",
        useAssociateTax: true,
      },
      usePartnerPremiumVoucher: false,
      voucherInfo: null as PremiumVoucherInfo | null,
      grantPartnerVoucherOpen: false,
      grantPartnerVoucherTargetId: "",
      grantingPartnerVoucher: false,
    };
  },
  computed: {
    canUsePremiumVouchers(): boolean {
      return this.$store.getters.canUsePremiumVouchers;
    },
    isAssociateUser(): boolean {
      return this.$store.getters.isAssociateProfile;
    },
    showPremiumUpsell(): boolean {
      return this.$store.getters.shouldShowPremiumUpsell;
    },
    isFranchiseAssociateInactive(): boolean {
      return this.$store.getters.isFranchiseAssociateInactive;
    },
    isFranchiseAssociatePending(): boolean {
      return this.$store.getters.isFranchiseAssociatePending;
    },
    hasNoFranchiseFilterProfile(): boolean {
      return this.$store.getters.hasNoFranchiseFilterProfile;
    },
    isCollaboratorManagerUser(): boolean {
      return this.$store.getters.isCollaboratorManager;
    },
    sessionFranchises(): SessionFranchiseType[] {
      return this.$store.getters.sessionFranchises;
    },
    isAdminForSelectedFranchise(): boolean {
      if (!this.franchiseId) return false;
      const franchise = this.sessionFranchises.find((f) => f.id === this.franchiseId);
      return franchise?.isAdmin ?? false;
    },
    canTrackAssociates(): boolean {
      return this.isAdminForSelectedFranchise || this.isCollaboratorManagerUser;
    },
    effectiveAssociateId(): string {
      if (this.canTrackAssociates) {
        return this.associateId;
      }
      return this.$store.state.session?.associateId ?? "";
    },
    associateFilterItems(): { text: string; value: string }[] {
      const todos = [{ text: "Todos", value: "" }];
      const items = this.associates.map((a) => ({ text: a.name, value: a.id }));
      return [...todos, ...items];
    },
    statusFilterItems(): { text: string; value: string }[] {
      return [
        { text: "Prospectado", value: "PROSPECTADO" },
        { text: "Pré-cadastro", value: "PRE_REGISTRATION" },
        { text: "Pendente", value: "PENDING" },
        { text: "Ativo", value: "ACTIVE" },
        { text: "Aguardando", value: "WAITING" },
        { text: "Bloqueado", value: "BLOCKED" },
        { text: "Cancelado", value: "CANCELED" },
      ];
    },
    documentMask(): string {
      const digits = (this.newPartner.document ?? "").replace(/\D/g, "");
      return digits.length > 11 ? "##.###.###/####-##" : "###.###.###-##";
    },
    grantPartnerVoucherCandidates(): { id: string; label: string }[] {
      return this.partners
        .filter((partner) => !partner.isPremium)
        .map((partner) => ({
          id: partner.id,
          label: `${partner.name} (${this.formatDocument(partner.document ?? "", partner.status)})`,
        }));
    },
  },
  watch: {
    franchiseId(newValue: string) {
      if (this.hasNoFranchiseFilterProfile) return;
      if (newValue) {
        this.associateId = "";
        if (this.canTrackAssociates) {
          this.handleLoadAssociates();
        }
        this.handleLoadPartners();
      } else {
        this.associates = [];
        this.associateId = "";
        this.partners = [];
      }
    },
    associateId() {
      if (this.hasNoFranchiseFilterProfile || this.franchiseId) {
        this.handleLoadPartners();
      }
    },
    statusFilter() {
      this.handleLoadPartners();
    },
    sessionFranchises: {
      handler() {
        this.initializeFranchiseSelection();
      },
      immediate: true,
    },
  },
  created() {
    void this.loadVoucherInfo();
    if (this.hasNoFranchiseFilterProfile) {
      this.handleLoadPartners();
      return;
    }
    this.initializeFranchiseSelection();
    if (!this.franchiseId) {
      this.loading = false;
    }
  },
  methods: {
    goToPremiumUpsell() {
      this.$router.push({ name: "associates", query: { adesao: "premium" } });
    },
    initializeFranchiseSelection() {
      if (this.franchiseId) return;
      const source = this.sessionFranchises;
      if (source.length > 0) {
        this.franchiseId = source[0].id;
      }
    },
    async handleLoadAssociates() {
      if (!this.franchiseId) return;
      const getAssociates = new GetAssociates();
      try {
        this.associates = await getAssociates.execute(this.franchiseId);
      } catch {
        this.associates = [];
      }
    },
    async handleLoadPartners() {
      this.loading = true;
      try {
        if (this.franchiseId || this.hasNoFranchiseFilterProfile) {
          const getAllPartners = new GetAllPartners();
          const associateIdParam = this.effectiveAssociateId || undefined;
          const franchiseIdParam = this.hasNoFranchiseFilterProfile
            ? undefined
            : this.franchiseId || undefined;
          this.partners = await getAllPartners.execute(
            franchiseIdParam,
            associateIdParam,
            this.statusFilter || undefined
          );
        } else {
          this.partners = [];
        }
      } catch {
        this.partners = [];
      } finally {
        this.loading = false;
      }
    },
    openNewPartnerDialog() {
      if (this.$store.getters.isFranchiseAssociateInactive) return;
      if (this.$store.getters.isFranchiseAssociatePending) {
        this.showSnack(
          "Seu cadastro ainda está pendente. Aguarde a aprovação para cadastrar parceiros.",
          "warning"
        );
        return;
      }
      this.resetNewPartner();
      this.openNewPartner = true;
    },
    resetNewPartner() {
      this.newPartner = {
        name: "",
        phone: "",
        email: "",
        document: "",
        useAssociateTax: true,
      };
      this.usePartnerPremiumVoucher = false;
    },
    async loadVoucherInfo() {
      if (!this.$store.getters.canUsePremiumVouchers) {
        this.voucherInfo = null;
        return;
      }
      try {
        this.voucherInfo = await new GetPremiumVouchers().execute();
      } catch {
        this.voucherInfo = null;
      }
    },
    openGrantPartnerVoucherDialog() {
      this.grantPartnerVoucherTargetId = "";
      this.grantPartnerVoucherOpen = true;
    },
    async handleGrantPartnerVoucher(partnerId: string) {
      this.grantingPartnerVoucher = true;
      try {
        await new GrantPartnerPremiumVoucher().execute(partnerId);
        this.showSnack("Voucher concedido com sucesso. O parceiro agora é Premium.", "success");
        await this.loadVoucherInfo();
        await this.handleLoadPartners();
        if (this.selectedPartner?.id === partnerId) {
          this.selectedPartner = {
            ...this.selectedPartner,
            isPremium: true,
          };
        }
        this.grantPartnerVoucherOpen = false;
      } catch {
        this.showSnack("Não foi possível conceder o voucher.", "error");
      } finally {
        this.grantingPartnerVoucher = false;
      }
    },
    async handleGrantPartnerVoucherFromDialog() {
      if (!this.grantPartnerVoucherTargetId) return;
      await this.handleGrantPartnerVoucher(this.grantPartnerVoucherTargetId);
    },
    async handleCreateSimplePartner() {
      if (this.$store.getters.isFranchiseAssociateInactive) {
        this.showSnack(
          "Sua conta está inativa. Apenas visualização é permitida. Contate o suporte.",
          "warning"
        );
        return;
      }
      if (this.$store.getters.isFranchiseAssociatePending) {
        this.showSnack(
          "Seu cadastro ainda está pendente. Aguarde a aprovação para cadastrar parceiros.",
          "warning"
        );
        return;
      }
      if (!this.hasNoFranchiseFilterProfile && !this.franchiseId) {
        this.showSnack("Selecione uma cidade antes de cadastrar.", "warning");
        return;
      }
      const payload: CreateSimplePartnerPayload = this.buildSimplePartnerPayload();
      this.savingPartner = true;
      try {
        const createPartner = new CreateSimplePartner();
        await createPartner.execute(payload);
        this.showSnack("Parceiro cadastrado com sucesso.", "success");
        await this.loadVoucherInfo();
        this.handleLoadPartners();
        this.openNewPartner = false;
      } catch {
        this.showSnack("Não foi possível cadastrar o parceiro.", "error");
      } finally {
        this.savingPartner = false;
      }
    },
    buildSimplePartnerPayload(): CreateSimplePartnerPayload {
      return {
        name: this.newPartner.name,
        phone: this.stripNonDigits(this.newPartner.phone),
        email: this.newPartner.email,
        document: this.stripNonDigits(this.newPartner.document),
        franchiseId: this.hasNoFranchiseFilterProfile ? undefined : this.franchiseId,
        useAssociateTax: this.newPartner.useAssociateTax,
        usePartnerPremiumVoucher: this.usePartnerPremiumVoucher,
      };
    },
    stripNonDigits(value: string): string {
      return value ? value.replace(/\D/g, "") : "";
    },
    showSnack(message: string, color: string) {
      this.$store.dispatch("showSnack", { message, color, timer: 4000 });
    },
    closeNewPartnerDialog() {
      this.openNewPartner = false;
      this.resetNewPartner();
    },
    handleDetailPartner(partner: PartnerWithDetail) {
      this.$router.push({ name: "partnerStore", params: { id: partner.id } });
    },
    closePartnerDetail() {
      this.openPartnerDetail = false;
      this.selectedPartner = null;
    },
    phoneNumberFormat: (value: string): string => {
      if (!value) return "";
      const phone = "(" + value.substr(0, 2) + ") " + value.substr(2, 5) + "-" + value.substr(7, 8);
      return phone;
    },
    formatDate: (date: string): string => {
      const dateFormated = format(new Date(date), "dd/MM/yyyy HH:mm:ss");
      return dateFormated;
    },
    formatDocument: (document: string, status?: string): string => {
      if (status === "PROSPECTADO" || document?.startsWith("P")) {
        return "—";
      }
      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (document.length === 14) {
        return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return document;
    },
    displayTextFranchise(item: SessionFranchiseType): string {
      if (item.cityName && item.state) {
        return `${item.cityName} - ${item.state}`;
      }
      return item.name ?? item.id;
    },
    partnerStatusLabel(status: string | undefined): string {
      if (status === "PROSPECTADO") return "Prospectado";
      if (status === "PRE_REGISTRATION") return "Pré-cadastro";
      if (status === "PENDING") return "Pendente";
      if (status === "ACTIVE") return "Ativo";
      if (status === "WAITING") return "Aguardando";
      if (status === "BLOCKED") return "Bloqueado";
      if (status === "CANCELED") return "Cancelado";
      return status ?? "-";
    },
  },
});
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
}

tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.premium-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;

  &--premium {
    background-color: #2e7d32;
  }

  &--free {
    background-color: #c62828;
  }
}

.voucher-card__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 1rem;
}
</style>
